/**
 * Mood analytics — PURE statistics, no AI (PRODUCT_PLAN: "generative AI
 * over content" is explicitly DISCARDED). Counts, averages,
 * trend and weekly/monthly aggregation. Everything deterministic and testable.
 *
 * Input is raw { entry_date (YYYY-MM-DD), mood (1–5) } rows or the
 * aggregated SQL distribution; presentation metrics are derived here.
 */
import { MOOD_MAX, MOOD_MIN, MOOD_VALUES, type MoodValue } from "./mood";

const PERCENT = 100;
const ROUND_AVG = 100; // 2 decimales

/** An entry with mood (raw input for analytics). */
export interface MoodEntry {
  entry_date: string | null;
  mood: number | null;
}

/** Count by mood value (1–5). */
export interface MoodDistributionItem {
  mood: MoodValue;
  count: number;
  /** % of all entries with a mood (0–100, rounded). */
  percent: number;
}

/** Point in the mood time series (month "YYYY-MM" or week "YYYY-Www"). */
export interface MoodTrendPoint {
  bucket: string;
  /** Average mood for the period (1–5, 2 decimals). null if empty. */
  average: number | null;
  count: number;
}

export interface MoodSummary {
  /** Total number of entries with an assigned mood. */
  total: number;
  /** Global average mood (1–5, 2 decimals). null if there is no data. */
  average: number | null;
  /** Most frequent mood value. null if there is no data. */
  mostFrequent: MoodValue | null;
  distribution: MoodDistributionItem[];
}

function round2(n: number): number {
  return Math.round(n * ROUND_AVG) / ROUND_AVG;
}

function isValidMood(value: number | null | undefined): value is MoodValue {
  return typeof value === "number" && value >= MOOD_MIN && value <= MOOD_MAX;
}

/**
 * Builds the complete distribution (always 5 rows, 1–5, with 0 where there is no
 * data) from aggregated { mood, count } pairs (e.g. from SQL).
 */
export function buildMoodDistribution(
  rows: { mood: number | null; count: number }[],
): MoodDistributionItem[] {
  const counts = new Map<MoodValue, number>();
  let total = 0;
  for (const row of rows) {
    if (!isValidMood(row.mood)) {
      continue;
    }
    const c = Math.max(0, Math.trunc(row.count));
    counts.set(row.mood, (counts.get(row.mood) ?? 0) + c);
    total += c;
  }
  return MOOD_VALUES.map((mood) => {
    const count = counts.get(mood) ?? 0;
    return {
      mood,
      count,
      percent: total > 0 ? Math.round((count / total) * PERCENT) : 0,
    };
  });
}

/** Full summary (total, average, mode, distribution) from the distribution. */
export function summarizeMood(rows: { mood: number | null; count: number }[]): MoodSummary {
  const distribution = buildMoodDistribution(rows);
  const total = distribution.reduce((acc, d) => acc + d.count, 0);
  if (total === 0) {
    return { total: 0, average: null, mostFrequent: null, distribution };
  }
  const weighted = distribution.reduce((acc, d) => acc + d.mood * d.count, 0);
  let mostFrequent: MoodValue = MOOD_VALUES[0];
  let max = -1;
  for (const d of distribution) {
    if (d.count > max) {
      max = d.count;
      mostFrequent = d.mood;
    }
  }
  return {
    total,
    average: round2(weighted / total),
    mostFrequent,
    distribution,
  };
}

/** Year + ISO-8601 week number for a day key (YYYY-MM-DD). */
function isoWeekBucket(dateKey: string): string | null {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) {
    return null;
  }
  // UTC midday: immune to DST/zone; the ISO week depends only on the date.
  const date = new Date(Date.UTC(y, m - 1, d));
  const day = date.getUTCDay() || 7; // domingo=0 → 7
  const THURSDAY = 4;
  date.setUTCDate(date.getUTCDate() + THURSDAY - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const MS_PER_DAY = 86_400_000;
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / MS_PER_DAY + 1) / 7);
  const PAD = 2;
  return `${date.getUTCFullYear()}-W${String(week).padStart(PAD, "0")}`;
}

function monthBucket(dateKey: string): string | null {
  const MONTH_LENGTH = 7;
  if (dateKey.length < MONTH_LENGTH) {
    return null;
  }
  return dateKey.slice(0, MONTH_LENGTH);
}

/**
 * Aggregates raw mood rows into time-series points by period (week or month),
 * sorted ascending. Calculates the average per bucket (2 decimals).
 */
export function bucketMoodByPeriod(
  entries: MoodEntry[],
  period: "week" | "month",
): MoodTrendPoint[] {
  const buckets = new Map<string, { sum: number; count: number }>();
  for (const e of entries) {
    if (!e.entry_date || !isValidMood(e.mood)) {
      continue;
    }
    const key = period === "week" ? isoWeekBucket(e.entry_date) : monthBucket(e.entry_date);
    if (!key) {
      continue;
    }
    const agg = buckets.get(key) ?? { sum: 0, count: 0 };
    agg.sum += e.mood;
    agg.count += 1;
    buckets.set(key, agg);
  }
  return [...buckets.entries()]
    .toSorted((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([bucket, agg]) => ({
      bucket,
      average: agg.count > 0 ? round2(agg.sum / agg.count) : null,
      count: agg.count,
    }));
}

/**
 * Trend between the endpoints of an average series: "up" if the last
 * exceeds the first by more than the threshold, "down" otherwise, "flat" if neither.
 * Simple statistics, no regression: the plan asks for "weekly/monthly trend".
 */
export function moodTrend(points: MoodTrendPoint[]): "up" | "down" | "flat" {
  const withData = points.filter(
    (p): p is MoodTrendPoint & { average: number } => p.average !== null,
  );
  const MIN_POINTS = 2;
  const firstPoint = withData[0];
  const lastPoint = withData.at(-1);
  if (withData.length < MIN_POINTS || !firstPoint || !lastPoint) {
    return "flat";
  }
  const first = firstPoint.average;
  const last = lastPoint.average;
  const THRESHOLD = 0.25;
  if (last - first > THRESHOLD) {
    return "up";
  }
  if (first - last > THRESHOLD) {
    return "down";
  }
  return "flat";
}
